import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Grid, Typography, Select, MenuItem, FormControl,
  InputLabel, Skeleton
} from '@mui/material';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import api from '../api/axios';
import {
  PageHeader, Panel, DataTable, StatusBadge, MetricCard, useFeedback
} from '../components/ui';
import { color } from '../theme/tokens';

const fmt = (n) => n != null ? `₹${Number(n).toLocaleString('en-IN')}` : '—';
const fmtShort = (n) => {
  if (!n) return '₹0';
  if (n >= 1e7) return `₹${(n/1e7).toFixed(1)}Cr`;
  if (n >= 1e5) return `₹${(n/1e5).toFixed(1)}L`;
  if (n >= 1e3) return `₹${(n/1e3).toFixed(1)}K`;
  return `₹${n}`;
};

// Restrained, token-derived palette for the category slices (solid colours only).
const CATEGORY_COLORS = [
  '#FFB800', // gold
  '#2E90FA', // info
  '#17B26A', // success
  '#F79009', // warning
  '#667085', // neutral
  '#98A2B3', // ink 400
];

const AXIS_TICK = { fontSize: 11, fill: color.ink[500] };
const TOOLTIP_PROPS = {
  contentStyle: { fontSize: 12, borderRadius: 8, border: `1px solid ${color.border}` },
  labelStyle: { fontSize: 12, color: color.ink[700] },
};

const Analytics = () => {
  const feedback = useFeedback();
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays]     = useState(30);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/admin/analytics', { params: { days } });
      setData(res.data.data);
    } catch { feedback.error('Failed to load analytics'); }
    finally { setLoading(false); }
  }, [days, feedback]);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  const summary = data?.summary;
  const products = (data?.topProducts ?? []).map((p, i) => ({ ...p, rank: i + 1 }));

  const metrics = [
    { label: 'Total Revenue',   value: fmtShort(summary?.total_revenue) },
    { label: 'Avg Order Value', value: fmtShort(summary?.avg_order_value) },
    { label: 'Bills Raised',    value: summary?.total_bills?.toLocaleString() },
    { label: 'Units Sold',      value: summary?.total_units_sold?.toLocaleString() },
    { label: 'Unique Products', value: summary?.unique_products_sold },
    { label: 'Active Branches', value: summary?.active_branches },
  ];

  const productColumns = [
    {
      key: 'rank', label: '#', width: 56,
      render: (v) => (
        <Typography component="span" sx={{ fontSize: '0.8125rem', fontWeight: 600, color: color.ink[500] }}>
          {v}
        </Typography>
      ),
    },
    {
      key: 'ItemName', label: 'Product',
      render: (v) => (
        <Typography component="span" sx={{ fontSize: '0.875rem', fontWeight: 500, color: color.ink[900] }}>
          {v ?? '—'}
        </Typography>
      ),
    },
    {
      key: 'BRAND', label: 'Brand',
      render: (v) => (v ? <StatusBadge label={v} tone="neutral" dot={false} /> : '—'),
    },
    {
      key: 'units_sold', label: 'Units', align: 'right',
      render: (v) => (v != null ? Number(v).toLocaleString('en-IN') : '—'),
    },
    {
      key: 'revenue', label: 'Revenue', align: 'right',
      render: (v) => (
        <Typography component="span" sx={{ fontSize: '0.8125rem', fontWeight: 700, color: color.ink[900] }}>
          {fmt(v)}
        </Typography>
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        eyebrow="Reports"
        title="Sales Analytics"
        subtitle="Revenue, category mix and best-selling products across all branches."
        actions={
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="period-label">Period</InputLabel>
            <Select
              labelId="period-label"
              value={days}
              label="Period"
              onChange={(e) => setDays(e.target.value)}
            >
              <MenuItem value={7}>Last 7 days</MenuItem>
              <MenuItem value={30}>Last 30 days</MenuItem>
              <MenuItem value={90}>Last 90 days</MenuItem>
              <MenuItem value={365}>Last 1 year</MenuItem>
            </Select>
          </FormControl>
        }
      />

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {metrics.map((m) => (
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }} key={m.label}>
            <MetricCard label={m.label} value={m.value} loading={loading} />
          </Grid>
        ))}
      </Grid>

      <Panel title="Daily Revenue" sx={{ mb: 3 }}>
        {loading ? (
          <Skeleton variant="rounded" height={260} />
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={data?.dailyRevenue} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={color.divider} vertical={false} />
              <XAxis dataKey="sale_date" tick={AXIS_TICK} tickFormatter={(d) => d?.slice(5)} tickMargin={8} />
              <YAxis tick={AXIS_TICK} tickFormatter={fmtShort} width={56} />
              <Tooltip formatter={(v) => fmt(v)} {...TOOLTIP_PROPS} />
              <Area
                type="monotone"
                dataKey="daily_revenue"
                stroke="#FFB800"
                strokeWidth={2}
                fill="#FFB800"
                fillOpacity={0.14}
                name="Revenue"
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </Panel>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 5 }}>
          <Panel title="Revenue by Category" sx={{ height: '100%' }}>
            {loading ? (
              <Skeleton variant="rounded" height={240} />
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie
                    data={data?.categoryBreakdown}
                    dataKey="revenue"
                    nameKey="MAINGROUP"
                    cx="50%"
                    cy="50%"
                    innerRadius={45}
                    outerRadius={78}
                    paddingAngle={2}
                    labelLine={false}
                    label={({ percent }) => (percent >= 0.04 ? `${Math.round(percent * 100)}%` : '')}
                  >
                    {data?.categoryBreakdown?.map((_, i) => (
                      <Cell key={i} fill={CATEGORY_COLORS[i % CATEGORY_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => fmt(v)} {...TOOLTIP_PROPS} />
                  <Legend
                    verticalAlign="bottom"
                    height={24}
                    iconType="circle"
                    wrapperStyle={{ fontSize: 11, color: color.ink[500] }}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Panel>
        </Grid>

        <Grid size={{ xs: 12, md: 7 }}>
          <Panel title="Top Brands by Revenue" sx={{ height: '100%' }}>
            {loading ? (
              <Skeleton variant="rounded" height={240} />
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={data?.topBrands?.slice(0, 8)} layout="vertical" margin={{ left: 20, right: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={color.divider} horizontal={false} />
                  <XAxis type="number" tick={AXIS_TICK} tickFormatter={fmtShort} />
                  <YAxis type="category" dataKey="BRAND" tick={AXIS_TICK} width={84} />
                  <Tooltip formatter={(v) => fmt(v)} {...TOOLTIP_PROPS} />
                  <Bar dataKey="revenue" fill="#FFB800" radius={[0, 4, 4, 0]} name="Revenue" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Panel>
        </Grid>
      </Grid>

      <Panel title="Top 10 Products" noPadding>
        <DataTable
          columns={productColumns}
          rows={products}
          rowKey="ITEMCODE"
          loading={loading}
          size="small"
          minWidth={640}
          emptyTitle="No products sold"
          emptyDescription="No product sales were recorded for the selected period."
          sx={{ border: 0, borderRadius: 0 }}
        />
      </Panel>
    </Box>
  );
};

export default Analytics;
